<template>
  <base-sticky :margin="header.sticky.margin" name="headerPLP">
    <div
      v-style:top="{ sm: '0', lg: '4.5rem' }"
      class="body-5 sticky z-header flex b-t b-grey-80 bg-white px-6 pb-3 pt-2 between <lg:hidden"
      :class="{ 'bg-op-80': overlayTitle }"
    >
      <div aria-live="polite">
        <p>
          {{ total }} {{ $pluralize($t.products, total) }}
        </p>
      </div>
      <div class="flex items-center <lg:row-span-2">
        <!-- Desktop sort/filter button -->
        <base-button
          aria-controls="plp-filters"
          :aria-expanded="filtersShown"
          class="flex items-center gap-1 <lg:hidden"
          @click="onFiltersToggle"
        >
          <span class="<lg:sr-only">
            {{ totalFiltersApplied ? `${$t.showFilters} (${totalFiltersApplied})` : $t.showFilters }}
          </span>
          <vf-icon name="filters" size="sm" />
        </base-button>
      </div>
    </div>
    <div
      :id="`${id}-content`"
      :aria-labelledby="id"
      class="relative flex gap-3 overflow-y-hidden px-6 py-4"
    >
      <base-button v-if="totalFiltersApplied" class="shrink-0 b-r b-grey-60 pr-3 text-sm underlined" @click="$emit('clear')">
        {{ $t.clearAll }}
      </base-button>
      <vf-pill
        v-for="({ code, options, type, selected }, i) in appliedFilters"
        :key="i + code"
        removable
        @click="$emit('remove', { code, id: options?.[0]?.id! })"
      >
        <span class="sr-only">{{ $t.removeAppliedFilter }}</span>
        <template v-if="options?.[0] && type === 'ColorSwatch'" #before>
          <vf-color-swatch
            :key="(options[0] as ColorOption).color.join('-')"
            :id="options[0].id"
            class="block h-4 w-4"
            class-inner="rounded-full overflow-hidden"
            :color="(options[0] as ColorOption).color"
          />
        </template>
        <template v-if="selected && type === 'TilePercentage' && !selected.includes('%')" #after>
          %
        </template>
        <template v-if="selected && type === 'PriceInput'">
          {{ useFormattedPrice(+selected[0], currency, { trailingZeroDisplay: 'stripIfInteger' }) }}
          -
          {{ useFormattedPrice(+selected[1], currency, { trailingZeroDisplay: 'stripIfInteger' }) }}
        </template>
        <template v-else-if="selected && type === 'Search'">
          {{ selected[0] }}
        </template>
        <template v-else-if="options">
          <template v-for="({ label, id: optionId }) in options" :key="optionId">
            {{ label }}
          </template>
        </template>
      </vf-pill>
    </div>
    <panel-filters v-slot="attrs">
      <slot v-bind="attrs" />
    </panel-filters>
  </base-sticky>

  <!-- Mobile sort/filter button -->
  <base-button
    aria-controls="plp-filters"
    :aria-expanded="filtersShown"
    class="fixed bottom-0 left-1/2 z-header flex items-center gap-1 rounded bg-brand-4 px-6 py-3 subtitle-5 c-brand-5 shadow-lg duration-300 lg:hidden -translate-x-1/2"
    :class="isScrolling ? 'translate-y-full' : 'mb-4'"
    @click="onFiltersToggle"
  >
    {{ totalFiltersApplied ? `${$t.showFilters} (${totalFiltersApplied})` : $t.showFilters }}
    <vf-icon name="filters" size="md" />
  </base-button>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#commerce/components/product/list/Header.vue'
import type { BaseOption, ColorOption, FilterItem } from '#types/filters'

const { currency, filters, list } = defineProps<Props>()

defineEmits<Emits>()

const { $t } = useNuxtApp()
const filtersQuery = useRouteQuery('filters')
const header = useHeaderStore()
const id = useId()

const filtersShown = defineModel('filtersShown')
const PanelFilters = createPanel('filters')

/**
 * Get the selected filter pills from the prop filters.
 * @returns [] - ComputedRef<FilterItem[]>
 */
const appliedFilters = computed(() =>
  Object.values(filters).flatMap((item: FilterItem & { options?: BaseOption[] }) => {
    const { type, code, selected } = item

    /**
     * Price filter and search filter are exceptions which don't have `options`,
     * For price: pill just needs the min&max price that are stored within the `selected` in the form of [min, max]
     * For search: pill just needs the search value which is stored within the `selected` in the form of [searchValue]
     * To satisfy the expected type, we still include an empty `options` array.
     */
    if ((type === 'PriceInput' || type === 'Search') && selected?.filter(Boolean).length) {
      return [{ type, code, selected, options: [] }]
    }

    /**
     * Filter out the selected options for each filter
     */
    const filteredOptions = item.options?.filter(({ id }) => selected?.includes(id)) || []

    /**
     * Flatten the `options` which are grouped in each FilterItem,
     * so the template can easily traverse the array without using a nested v-for
     */
    return filteredOptions.map((option) => ({
      type,
      code,
      selected,
      options: [option]
    }))
  })
)

const total = computed(() => list?.total || 0)
const countAppliedFilters = (query: any): number | null => {
  const filters = typeof query === 'string' ? JSON.parse(query || '{}') : query || {}
  const count = Object.values(filters).reduce((sum: number, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
  return count > 0 ? count : null
}
const totalFiltersApplied = computed(() => countAppliedFilters(filtersQuery.value))

const onFiltersToggle = () => PanelFilters.open()

const { isScrolling } = useScroll(window, { idle: 150 })
</script>
